<template>
  <div class="bio-section">
    <div class="md-layout md-gutter moreInfoGroup">
      <div class="md-layout-item md-size-50">
        <md-field>
          <select v-model="$store.state.plot.localization.bio_actual_vegetation_anx11" name="actualVegetation">
            <option disabled value="">{{ $t("localization.actualVegetation") }}</option>
            <option v-for="(av, key) in actualVegetationList" :value="key" :key="key">{{ av }}</option>
          </select>
        </md-field>
      </div>
      <div class="md-layout-item md-size-50">
        <md-field>
          <select v-model="$store.state.plot.localization.bio_potential_vegetation_anx11" name="potentialVegetation">
            <option disabled value="">{{ $t("localization.potentialVegetation") }}</option>
            <option v-for="(pv, key) in potentialVegetationList" :value="key" :key="key">{{ pv }}</option>
          </select>
        </md-field>
      </div>

      <div class="md-layout-item md-size-50">
        <md-field>
          <select v-model="$store.state.plot.localization.bio_naturality" name="naturality">
            <option disabled value="">{{ $t("localization.naturality") }}</option>
            <option v-for="(n, key) in naturalityList" :value="key" :key="key">{{ n }}</option>
          </select>
        </md-field>
      </div>
      <div class="md-layout-item md-size-50">
        <md-field>
          <select v-model="$store.state.plot.localization.bio_biodiversity_risk" name="biodiversityRisk">
            <option disabled value="">{{ $t("localization.biodiversityRisk") }}</option>
            <option v-for="(rfb, key) in biodiversityRiskList" :value="key" :key="key">{{ rfb }}</option>
          </select>
        </md-field>
      </div>

      <div class="md-layout-item md-size-100">
        <md-field>
          <std-autocomplete
            v-if="loadStd"
            :data="biotopeList"
            :label="$t('localization.biotope')"
            :initialValue="$store.state.plot.localization.bio_biotope_std5"
            :initialDisplay="initBiotope"
            @result="getBiotope"></std-autocomplete>
        </md-field>
      </div>
    </div>

    <div v-for="(h, i) in $store.state.plot.localization.bio_hcv" class="md-layout md-gutter" :key="i">
      <div class="md-layout-item md-size-70" v-if="h !== null">
        <md-field>
          <select v-model="h.hcv" name="hcv">
            <option disabled value="">{{ $t("localization.hcv") }}</option>
            <option v-for="(hcv, code) in hcvList" :value="code" :key="code">[{{ code }}] {{ hcv }}</option>
          </select>
        </md-field>
      </div>
      <div class="md-layout-item md-size-20" v-if="h !== null">
        <md-field :class="!checkRange(h.dead_wood, 0, 10) ? 'md-invalid' : ''">
          <label for="deadWood">{{ $t("localization.deadWood") }} N°</label>
          <md-input type="number" name="deadWood" v-model="h.dead_wood" />
          <span class="md-error">{{ $t("error.errorRange") }} (0-10)</span>
        </md-field>
      </div>
      <div class="md-layout-item md-size-10" v-if="h !== null">
        <md-icon @click.native="removeHcv(i)" v-show="btn.rm">clear</md-icon>
      </div>

      <div class="md-layout-item md-size-10 tog-label" v-if="h !== null">
        <label>{{ $t("localization.endangeredType") }}</label>
      </div>
      <div class="md-layout-item md-size-25 tog-btn" v-if="h !== null">
        <toggle-buttons :btnList="endangeredTypeList" @btnValue="getEndangeredType" :index="i" :editTB="h.endangered_type" />
      </div>

      <div class="md-layout-item md-size-50" v-if="h !== null">
        <md-field v-show="h.endangered_type === $store.state.endangered_type.plants">
          <std-autocomplete
            v-if="loadPlants"
            :data="endangeredPlantsList"
            :label="$t('localization.endangeredPlants')"
            :initialValue="h.endangered_specie"
            :initialDisplay="initPlants[i]"
            :index="i"
            @result="getEndangeredSpecie"></std-autocomplete>
        </md-field>
        <md-field v-show="h.endangered_type === $store.state.endangered_type.animals">
          <std-autocomplete
            v-if="loadAnimals"
            :data="endangeredAnimalsList"
            :label="$t('localization.endangeredAnimals')"
            :initialValue="h.endangered_specie"
            :initialDisplay="initAnimals[i]"
            :index="i"
            @result="getEndangeredSpecie"></std-autocomplete>
        </md-field>
      </div>
      <div class="md-layout-item md-size-15" v-if="h !== null">
        <md-field :class="!checkPercentage(h.habitat) ? 'md-invalid' : ''" style="margin-top: 12px">
          <label for="habitat">{{ $t("localization.habitat") }} %</label>
          <md-input type="number" name="habitat" v-model="h.habitat" />
          <span class="md-error">{{ $t("error.errorRange") }} (0-100)</span>
        </md-field>
      </div>
    </div>

    <div class="md-layout md-gutter md-alignment-center-center">
      <md-button class="md-raised md-primary" @click="addHcv()" v-show="btn.add">{{ $t("global.add") }} {{ $t('localization.hcv') }}</md-button>
    </div>
  </div>
</template>

<script>
import StdAutocomplete from '@/components/StdAutocomplete'
import ToggleButtons from '@/components/ToggleButtons'

import { getDomain } from '@/assets/scripts/domainsManager'
import { checkPercentage, checkRange, formatList, getValue } from '@/assets/scripts/formatData'

const ACTUAL_VEGETATION = 'anx11'
const POTENTIAL_VEGETATION = 'anx11'
const NATURALITY = 'naturality_degree'
const BIOTOPE = 'std5'
const BIODIVERSITY_RISK = 'biodiversity_risk'
const HCV = 'hcv'
const ENDANGERED_TYPE = 'endangered_specie_type'
const ENDANGERED_PLANTS = 'endangered_specie_plant'
const ENDANGERED_ANIMALS = 'endangered_specie_animal'

export default {
  name: 'Biodiversity',
  props: ['edit'],
  components: {
    StdAutocomplete,
    ToggleButtons
  },
  data: () => ({
    actualVegetationList: {},
    potentialVegetationList: {},
    naturalityList: {},
    biotopeList: {},
    biodiversityRiskList: {},
    hcvList: {},
    endangeredTypeList: {},
    endangeredPlantsList: {},
    endangeredAnimalsList: {},
    btn: {
      rm: false,
      add: true
    },
    initBiotope: '',
    initPlants: [],
    initAnimals: [],
    loadStd: false,
    loadPlants: false,
    loadAnimals: false,
    checkPercentage: checkPercentage,
    checkRange: checkRange
  }),
  mounted () {
    this.setLists()
    this.setHcv()
  },
  methods: {
    setLists: function () {
      getDomain(ACTUAL_VEGETATION).then(data => {
        this.actualVegetationList = data[this.$i18n.locale]
      })
      getDomain(POTENTIAL_VEGETATION).then(data => {
        this.potentialVegetationList = data[this.$i18n.locale]
      })
      getDomain(NATURALITY).then(data => {
        this.naturalityList = data[this.$i18n.locale]
      })
      getDomain(BIOTOPE).then(data => {
        this.biotopeList = formatList(data[this.$i18n.locale])
        if (this.edit || this.$store.state.plot.localization.bio_biotope_std5.length > 0) {
          this.initBiotope = getValue(this.biotopeList, this.$store.state.plot.localization.bio_biotope_std5)
        }
        this.loadStd = true
      })
      getDomain(BIODIVERSITY_RISK).then(data => {
        this.biodiversityRiskList = data[this.$i18n.locale]
      })
      getDomain(HCV).then(data => {
        this.hcvList = data[this.$i18n.locale]
      })
      getDomain(ENDANGERED_TYPE).then(data => {
        this.endangeredTypeList = data[this.$i18n.locale]
      })
      getDomain(ENDANGERED_PLANTS).then(data => {
        this.endangeredPlantsList = formatList(data[this.$i18n.locale])
        if (this.edit || this.$store.state.plot.localization.bio_hcv[0].endangered_specie.length > 0) {
          this.$store.state.plot.localization.bio_hcv.forEach((hcv, i) => {
            if (hcv.endangered_type === this.$store.state.endangered_type.plants) {
              this.initPlants[i] = getValue(this.endangeredPlantsList, hcv.endangered_specie)
            }
          })
        }
        this.loadPlants = true
      })
      getDomain(ENDANGERED_ANIMALS).then(data => {
        this.endangeredAnimalsList = formatList(data[this.$i18n.locale])
        if (this.edit || this.$store.state.plot.localization.bio_hcv[0].endangered_specie.length > 0) {
          this.$store.state.plot.localization.bio_hcv.forEach((hcv, i) => {
            if (hcv.endangered_type === this.$store.state.endangered_type.animals) {
              this.initAnimals[i] = getValue(this.endangeredAnimalsList, hcv.endangered_specie)
            }
          })
        }
        this.loadAnimals = true
      })
    },

    addHcv: function () {
      this.$store.state.plot.localization.bio_hcv.push({ ...this.$store.state.plot.hcv })
      this.btn.rm = true
      if (this.getLength() === 3) {
        this.btn.add = false
      }
    },

    removeHcv: function (index) {
      this.$set(this.$store.state.plot.localization.bio_hcv, index, null)
      this.btn.add = true
      if (this.getLength() === 1) {
        this.btn.rm = false
      }
    },

    setHcv: function () {
      if (this.getLength() > 1) {
        this.btn.rm = true
        if (this.getLength() === 3) {
          this.btn.add = false
        }
      }
    },

    getBiotope: function (biotope) {
      this.$store.state.plot.localization.bio_biotope_std5 = biotope
    },

    getEndangeredSpecie: function (value, index) {
      this.$store.state.plot.localization.bio_hcv[index].endangered_specie = value
    },

    getEndangeredType: function (value, index) {
      this.$store.state.plot.localization.bio_hcv[index].endangered_type = value
    },

    getLength: function () {
      let length = 0
      this.$store.state.plot.localization.bio_hcv.filter(el => {
        if (el != null) length++
      })

      return length
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/style/arcadia_theme.scss';

  .bio-section {
    margin-left: 20px;
    margin-right: 20px;
  }

  .tog-btn {
    margin-top: -5px;
  }

  .tog-label {
    margin-top: 20px;
  }
</style>
